/**
 * 인증 상태 관리 (P6 — Admin Login)
 *
 * - localStorage 에 토큰 + must_change_password 저장
 * - 다른 탭과 자동 동기화 (storage 이벤트)
 * - API 클라이언트가 매 요청마다 getAccessToken() 호출
 * - 401 응답 시 clearAuth + LoginView redirect (api/client.ts 가 처리)
 *
 * 보안 노트:
 *   localStorage 는 XSS 에 취약 — 향후 httpOnly cookie 로 옮길 수 있음.
 *   현재는 admin 사이트 (내부 직원 사용) 이라 localStorage 로 단순화.
 */

import { z } from 'zod';

const STORAGE_KEY = 'hcltech_auth';

/**
 * localStorage 에 저장되는 인증 상태. backend LoginResponse 와 호환.
 * localStorage 는 외부 경계 — 읽을 때 safeParse 1회로 검증(경계 원칙).
 * 이 형식의 정본은 UI(저장도 UI가 하므로) — shared 아님.
 */
const AuthStateSchema = z.object({
  accessToken:          z.string().min(1),
  refreshToken:         z.string().min(1),
  accessExpiresAt:      z.string(),        // ISO 8601
  refreshExpiresAt:     z.string(),        // ISO 8601
  must_change_password: z.boolean(),
  email:                z.string(),        // 표시용 (JWT decode 필요 없게)
});
export type AuthState = z.infer<typeof AuthStateSchema>;

/** 인증 상태 변경 알림. */
type AuthListener = (state: AuthState | null) => void;
const listeners = new Set<AuthListener>();

// ─── 1) 저장 / 조회 ──────────────────────────────────────────────────
export function getAuthState(): AuthState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    // 외부 경계(localStorage) — safeParse 1회. 깨진/구버전 형식이면 정리하고 미인증 취급.
    const r = AuthStateSchema.safeParse(JSON.parse(raw));
    if (!r.success) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return r.data;
  } catch {
    return null;
  }
}

export function setAuthState(state: AuthState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  notify(state);
}

export function clearAuth(): void {
  localStorage.removeItem(STORAGE_KEY);
  notify(null);
}

// ─── 2) 편의 함수 ────────────────────────────────────────────────────
/** 현재 access token (없으면 null). API 클라이언트가 호출. */
export function getAccessToken(): string | null {
  return getAuthState()?.accessToken ?? null;
}

/**
 * 로그인 상태 — accessToken 존재 + 만료 안 됨.
 *
 * 만료 30초 전 buffer — refresh 로 갱신할 수 있게 여유.
 * 실제로는 401 시 client.ts 가 자동 logout 하므로 이건 단순 check.
 */
export function isLoggedIn(): boolean {
  const state = getAuthState();
  if (!state) return false;
  const expiresAt = new Date(state.accessExpiresAt).getTime();
  return expiresAt > Date.now() + 30_000;
}

/** 비밀번호 변경 필요 — login 응답의 must_change_password 그대로. */
export function needsPasswordChange(): boolean {
  return getAuthState()?.must_change_password === true;
}

/** 표시용 이메일 (사이드바, 헤더 등). */
export function getCurrentEmail(): string | null {
  return getAuthState()?.email ?? null;
}

/**
 * 현재 사용자 role — accessToken(JWT) 클레임에서 디코드.
 *   서버 권한이 진짜 게이트(requireRole). 이건 UI 표시용(owner 전용 섹션 노출 등).
 *   디코드 실패/토큰 없음 → null.
 */
export function getCurrentRole(): string | null {
  const token = getAuthState()?.accessToken;
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    // JWT payload도 외부 경계(토큰은 저장소에서 옴) — 필요한 클레임만 safeParse
    const r = z.object({ role: z.string() }).safeParse(
      JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))),
    );
    return r.success ? r.data.role : null;
  } catch {
    return null;
  }
}

// ─── 3) 변경 알림 ────────────────────────────────────────────────────
/**
 * 인증 상태 변경 구독.
 *
 * 사용:
 *   const off = onAuthChanged((s) => render());
 *   onUnmount(() => off());
 */
export function onAuthChanged(listener: AuthListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(state: AuthState | null): void {
  for (const fn of listeners) {
    try { fn(state); } catch { /* ignore */ }
  }
}

// 다른 탭에서 로그아웃하면 자동 반영
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      notify(getAuthState());
    }
  });
}

// ─── 4) 라우터 가드 헬퍼 ──────────────────────────────────────────────
/**
 * 인증 페이지 (로그인/비번변경) 인지 검사.
 * 라우터 가드에서 사용.
 */
export function isAuthRoute(pathname: string): boolean {
  return pathname === '/login' || pathname === '/change-password';
}

/**
 * 가격 정책(임대 계산식 프리셋)에 들어갈 수 있는가 — 최고관리자·책임자만.
 *
 * 서안드레 2026-08-20: "임대계산식 프리셋 진입은 최고관리자/책임자가 가능하도록".
 * 현민: 열람도 막고 카드 자체를 안 보이게.
 *
 * ★ 이건 **UI 노출용**이다. 진짜 게이트는 백엔드의
 *   `requireRole('owner', 'leader')` 다(rental-preset.routes.ts 가 모듈 전체에
 *   use('*') 로 건다). 여기서 어쩌다 통과시켜도 API 가 403 을 낸다 — 화면만
 *   고쳐 막는 방식은 URL 을 직접 치면 뚫린다.
 *
 * ★ 역할 목록을 여기 한 곳에만 둔다. 설정 카드와 라우트 가드가 각자 `role ===`
 *   을 적어 두면 다음에 한쪽만 고치게 된다.
 */
export function canManagePricing(): boolean {
  const role = getCurrentRole();
  return role === 'owner' || role === 'leader';
}

/**
 * 첫 로드 시 적절한 페이지로 redirect (URL 정합).
 *
 * 호출 위치: main.ts 의 라우터 초기화 직전.
 *   - 로그인 안 됨 → /login
 *   - 비번 변경 필요 → /change-password
 *   - 정상 → 현재 URL 유지
 */
export function applyInitialAuthRedirect(): void {
  const path = location.pathname;

  // 인증 페이지인데 이미 로그인됨 → 적절한 곳으로
  if (path === '/login' && isLoggedIn()) {
    if (needsPasswordChange()) {
      history.replaceState(null, '', '/change-password');
    } else {
      history.replaceState(null, '', '/');
    }
    return;
  }
  if (path === '/change-password' && isLoggedIn() && !needsPasswordChange()) {
    history.replaceState(null, '', '/');
    return;
  }

  // 보호 페이지인데 인증 안 됨 → /login
  if (!isAuthRoute(path)) {
    if (!isLoggedIn()) {
      // 원래 가려던 URL 을 localStorage 에 임시 저장 (로그인 후 복귀)
      sessionStorage.setItem('hcltech_post_login_redirect', path + location.search);
      history.replaceState(null, '', '/login');
    } else if (needsPasswordChange()) {
      history.replaceState(null, '', '/change-password');
    }
  }
}

/**
 * 로그인 / 비번변경 성공 후 복귀할 URL.
 *
 * - 로그인 전에 가려던 URL 이 있으면 그것 (sessionStorage)
 * - 없으면 /
 */
export function consumePostLoginRedirect(): string {
  const saved = sessionStorage.getItem('hcltech_post_login_redirect');
  sessionStorage.removeItem('hcltech_post_login_redirect');
  return saved ?? '/';
}
